import React, { ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { queryKeysClient } from 'common/clients/query-key.client';
import { queryClient } from 'common/clients/query.client';
import { App } from 'common/components/antd/App';
import { Button } from 'common/components/antd/Button/Button';
import { Form } from 'common/components/antd/Form/Form';
import { Input } from 'common/components/antd/Input/Input';
import { Select } from 'common/components/antd/Select/Select';
import { Footer, Header, EnhancedModal } from 'common/components/EnhancedModal/EnhancedModal';
import { ENV_API_TYPE, ENV_ASSET_ISSUER, ENV_COUNTERPARTY_GUID } from 'common/constants/env.constants';
import { IS_ADMIN_FE } from 'common/constants/general-access-role.constants';
import { EApiType } from 'common/enums/api-type.enum';
import { EQueryKeys } from 'common/enums/query-kyes.enum';
import { IBANFormItemRules } from 'common/utils/form.utils';
import { notifySuccess } from 'common/utils/notifier.utils';
import { useGetAssetsListQuery } from 'domains/assets/queries/assets.query';
import { TAssetEntity } from 'domains/assets/types/assets.types';
import {
  useGetCreateCustomerMutation,
  useUpdateCreateCustomerMutation,
} from 'domains/customers/queries/customers.query';
import { TCreateCustomersReqData, TUpdateCustomersReqData } from 'domains/customers/types/customers.types';
import { PRE_SELECTED_SEGMENT_VALUE } from './constants';
import { SFormContainer, SFooter, STitle, SSection } from './modal.style';
import { ECustomersFormItemNames, EModalType } from '../enums/customer.enum';
import { TCustomersModalData } from '../types/customers.types';

type TProps = {
  isOpen: boolean;
  onCancelModalHandler: () => void;
  data: TCustomersModalData;
};

const UserManagementCreateCustomersModal = (props: TProps): ReactElement => {
  const {
    isOpen,
    onCancelModalHandler,
    data: { rowData, type: modalType, branchesData, counterpartiesData },
  } = props;
  const { notification } = App.useApp();
  const { t } = useTranslation();
  const [isSubmitBtnLoading, setIsSubmitBtnLoading] = useState<boolean>(false);
  const [form] = Form.useForm();
  const [selectedCounterpartyGuid, setSelectedCounterpartyGuid] = useState(
    ENV_API_TYPE !== EApiType.Admin ? ENV_COUNTERPARTY_GUID : '',
  );

  const {
    data: assetsData,
    isError: isAssetsDataError,
    error: assetsDataError,
    isLoading: isAssetsDataLoading,
  } = useGetAssetsListQuery(
    selectedCounterpartyGuid,
    {},
    {
      enabled: !!selectedCounterpartyGuid,
      refetchOnMount: false,
    },
  );

  const { mutateAsync: mutateAsyncCreateCustomerMutation } = useGetCreateCustomerMutation();

  const { mutateAsync: mutateAsyncUpdateCustomerMutation } = useUpdateCreateCustomerMutation();

  useEffect(() => {
    if (isAssetsDataError) {
      notification.error({
        message: (assetsDataError as { message: string }).message,
        type: 'error',
      });
    }
  }, [isAssetsDataError, assetsDataError]);

  useEffect(() => {
    if (Object.keys(rowData || {}).length) {
      const {
        counterparty_guid,
        branch_name,
        segment,
        iban,
        balance_asset_code,
        name,
        surname,
        paternal_name,
        login,
        phone_number,
      } = rowData;

      form.setFields([
        {
          name: ECustomersFormItemNames.Counterparty,
          value: counterparty_guid,
        },
        {
          name: ECustomersFormItemNames.Branch,
          value: branch_name,
        },
        {
          name: ECustomersFormItemNames.Segment,
          value: segment,
        },
        {
          name: ECustomersFormItemNames.AssetCode,
          value: balance_asset_code,
        },
        {
          name: ECustomersFormItemNames.IBAN,
          value: iban,
        },
        {
          name: ECustomersFormItemNames.Name,
          value: name,
        },
        {
          name: ECustomersFormItemNames.ExternalID,
          value: login,
        },
        {
          name: ECustomersFormItemNames.Surname,
          value: surname,
        },
        {
          name: ECustomersFormItemNames.Phone,
          value: phone_number,
        },
        {
          name: ECustomersFormItemNames.PaternalName,
          value: paternal_name,
        },
      ]);
    }
  }, [rowData]);

  const segmentOptions = [
    {
      label: 'PL',
      value: 'PL',
    },
    {
      label: 'PA',
      value: 'PA',
    },
    {
      label: 'PB',
      value: 'PB',
    },
    { label: PRE_SELECTED_SEGMENT_VALUE, value: PRE_SELECTED_SEGMENT_VALUE },
  ];

  const onCloseModalHandler = (): void => {
    form.setFields([
      {
        name: ECustomersFormItemNames.Counterparty,
        value: [],
      },
      {
        name: ECustomersFormItemNames.Branch,
        value: [],
      },
      {
        name: ECustomersFormItemNames.Segment,
        value: PRE_SELECTED_SEGMENT_VALUE,
      },
      {
        name: ECustomersFormItemNames.AssetCode,
        value: [],
      },
      {
        name: ECustomersFormItemNames.IBAN,
        value: '',
      },
      {
        name: ECustomersFormItemNames.Name,
        value: '',
      },
      {
        name: ECustomersFormItemNames.ExternalID,
        value: '',
      },
      {
        name: ECustomersFormItemNames.Surname,
        value: '',
      },
      {
        name: ECustomersFormItemNames.Phone,
        value: '',
      },
      {
        name: ECustomersFormItemNames.PaternalName,
        value: '',
      },
    ]);

    onCancelModalHandler();
  };

  const onChangeFormHandler = (formData: Record<ECustomersFormItemNames, any>): void => {
    const { counterparty } = formData;

    if (counterparty) {
      setSelectedCounterpartyGuid(counterparty);
    }
  };

  const onFinishFormHandler = async (formData: Record<ECustomersFormItemNames, any>): Promise<void> => {
    try {
      setIsSubmitBtnLoading(true);

      const { counterparty, branch_guid, segment, asset_code, iban, name, external_id, surname, phone, paternal_name } =
        formData;

      if (modalType === EModalType.Edit) {
        const updateCustomerReqData: TUpdateCustomersReqData = {
          name,
          surname,
          paternal_name,
          phone_number: phone,
          branch_guid,
          segment,
          external_id,
          login: external_id,
        };
        if (iban) updateCustomerReqData.iban = iban;
        await mutateAsyncUpdateCustomerMutation({
          counterpartyGUID: counterparty,
          customerGUID: rowData.wallet_user_guid,
          requestData: updateCustomerReqData,
        });
        notifySuccess('usrRoleMng.customers.modal.edit.notification.success');
      } else {
        const createCustomerReqData: TCreateCustomersReqData = {
          name,
          surname,
          paternal_name,
          phone_number: phone,
          branch_guid,
          segment,
          asset_code,
          asset_issuer: ENV_ASSET_ISSUER,
          external_id,
          login: external_id,
        };
        if (iban) createCustomerReqData.iban = iban;
        await mutateAsyncCreateCustomerMutation({
          counterpartyGUID: counterparty,
          requestData: createCustomerReqData,
        });
        notifySuccess('usrRoleMng.customers.modal.create.notification.success');
      }

      await queryClient.invalidateQueries({
        queryKey: queryKeysClient[EQueryKeys.CustomerQueryKeys].getCustomersList_MultiRole._def,
      });

      setIsSubmitBtnLoading(false);
      onCancelModalHandler();
    } catch (error) {
      console.error(error);
      setIsSubmitBtnLoading(false);
    }
  };

  useEffect(() => {
    /**
     * @task DCM-366: preselect the counterparty value automatically for own counterparty
     *  */
    form.setFieldValue(ECustomersFormItemNames.Counterparty, ENV_COUNTERPARTY_GUID);
    /**
     * @task DCM-511: hardcode "PI" as value for segment in user-mng for customer
     *  */
    form.setFieldValue(ECustomersFormItemNames.Segment, PRE_SELECTED_SEGMENT_VALUE);
  }, [isOpen]);

  return (
    <EnhancedModal width={764} open={isOpen} onCancel={onCloseModalHandler}>
      <Form form={form} onValuesChange={onChangeFormHandler} onFinish={onFinishFormHandler}>
        <Header>
          {modalType === EModalType.Edit
            ? t('usrRoleMng.customers.modal.edit.title')
            : t('usrRoleMng.customers.modal.create.title')}
        </Header>

        <SSection>
          <STitle>
            {modalType === EModalType.Edit
              ? t('usrRoleMng.customers.modal.edit.section.title.user')
              : t('usrRoleMng.customers.modal.create.section.title.user')}
          </STitle>
        </SSection>

        <SFormContainer>
          <Form.Item
            name={ECustomersFormItemNames.Counterparty}
            label={t('usrRoleMng.customers.modal.create.label.counterparty')}
          >
            <Select
              style={{ width: '100%' }}
              size="large"
              disabled={modalType !== EModalType.Create || !IS_ADMIN_FE}
              placeholder={t('usrRoleMng.customers.modal.create.placeholder.counterparty')}
            >
              {(counterpartiesData?.counterparties || []).map(({ guid, name }) => (
                <Select.Option key={guid} value={guid}>
                  {name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name={ECustomersFormItemNames.Branch}
            label={t('usrRoleMng.customers.modal.create.label.branchName')}
          >
            <Select
              style={{ width: '100%' }}
              size="large"
              placeholder={t('usrRoleMng.customers.modal.create.placeholder.branchName')}
            >
              {(branchesData?.branches || []).map((option) => (
                <Select.Option key={option?.guid} value={option.guid}>
                  {option.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name={ECustomersFormItemNames.Segment}
            label={t('usrRoleMng.customers.modal.create.label.segment')}
          >
            <Select
              style={{ width: '100%' }}
              size="large"
              disabled
              placeholder={t('usrRoleMng.customers.modal.create.placeholder.segment')}
            >
              {(segmentOptions || []).map((item) => (
                <Select.Option key={item.value} value={item.value}>
                  {item.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name={ECustomersFormItemNames.AssetCode}
            label={t('usrRoleMng.customers.modal.create.label.asset')}
          >
            <Select
              style={{ width: '100%' }}
              size="large"
              placeholder={t('usrRoleMng.customers.modal.create.placeholder.asset')}
              loading={isAssetsDataLoading}
            >
              {(assetsData?.stellar_assets || []).map((item: TAssetEntity) => (
                <Select.Option key={item.asset_issuer} value={item.asset_code}>
                  {item.asset_code}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            rules={IBANFormItemRules}
            name={ECustomersFormItemNames.IBAN}
            label={t('usrRoleMng.customers.modal.create.label.iban')}
          >
            <Input size="large" placeholder={t('usrRoleMng.customers.modal.create.placeholder.iban') as string} />
          </Form.Item>
        </SFormContainer>

        <SSection>
          <STitle>
            {modalType === EModalType.Edit
              ? t('usrRoleMng.customers.modal.edit.section.title.personalInfo')
              : t('usrRoleMng.customers.modal.create.section.title.personalInfo')}
          </STitle>
        </SSection>

        <SFormContainer>
          <Form.Item name={ECustomersFormItemNames.Name} label={t('usrRoleMng.customers.modal.create.label.name')}>
            <Input placeholder={t('usrRoleMng.customers.modal.create.placeholder.name') as string} size="large" />
          </Form.Item>
          <Form.Item
            name={ECustomersFormItemNames.ExternalID}
            label={t('usrRoleMng.customers.modal.create.label.externalId')}
          >
            <Input
              size="large"
              disabled={modalType !== EModalType.Create}
              placeholder={t('usrRoleMng.customers.modal.create.placeholder.externalId') as string}
            />
          </Form.Item>
          <Form.Item
            name={ECustomersFormItemNames.Surname}
            label={t('usrRoleMng.customers.modal.create.label.surname')}
          >
            <Input size="large" placeholder={t('usrRoleMng.customers.modal.create.placeholder.surname') as string} />
          </Form.Item>
          <Form.Item
            name={ECustomersFormItemNames.Phone}
            label={t('usrRoleMng.customers.modal.create.label.phoneNumber')}
          >
            <Input
              type="tel"
              size="large"
              placeholder={t('usrRoleMng.customers.modal.create.placeholder.phoneNumber') as string}
            />
          </Form.Item>
          <Form.Item
            name={ECustomersFormItemNames.PaternalName}
            label={t('usrRoleMng.customers.modal.create.label.paternalName')}
          >
            <Input
              size="large"
              placeholder={t('usrRoleMng.customers.modal.create.placeholder.paternalName') as string}
            />
          </Form.Item>
        </SFormContainer>

        <Footer>
          <SFooter>
            <Button style={{ marginRight: 12 }} onClick={onCloseModalHandler}>
              {t('usrRoleMng.customers.modal.create.button.cancel')}
            </Button>
            <Button loading={isSubmitBtnLoading} htmlType="submit" type="primary">
              {modalType === EModalType.Edit
                ? t('usrRoleMng.customers.modal.edit.button.edit')
                : t('usrRoleMng.customers.modal.create.button.create')}
            </Button>
          </SFooter>
        </Footer>
      </Form>
    </EnhancedModal>
  );
};

export default UserManagementCreateCustomersModal;
